import { formatAED, usdToFils, type AdminCostRow } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { DataTable, ErrorState, Notice, PageHeader, Select, StatTile, Toolbar, type DataTableColumn } from "@teamna/ui/components/index.ts";
import { useAdminCosts } from "../lib/cc-api.ts";
import { formatUsd } from "./assistant-format.ts";

/**
 * Costs (`/costs`): the Twilio balance now, and what Twilio and the AI cost for each business in a
 * month (`core/services/business-costs.ts`). The Twilio cost of a business is its share of the
 * month's real Twilio bill, by the phone sign-ins of its people; the AI cost is exact.
 */

const MONTHS_BACK = 6;

/** The last six months, newest first, as `YYYY-MM` in UTC (Twilio counts its days in UTC). */
function recentMonths(now: Date = new Date()): string[] {
  return Array.from({ length: MONTHS_BACK }, (_, back) => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 1)).toISOString().slice(0, 7));
}

function monthName(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

function aed(dollars: number): string {
  return formatAED(usdToFils(dollars), { trimZeroFils: false });
}

/** A Twilio dollar amount in the same form as the table. */
function usd(dollars: number): string {
  return formatUsd(Math.round(dollars * 1_000_000));
}

/** AI plus Twilio, or null when the Twilio cost is not known (never a part shown as the whole). */
const total = (row: AdminCostRow) => (row.twilioMicros === null ? null : row.aiMicros + row.twilioMicros);

const rowName = (row: AdminCostRow) => (row.businessId === null ? "No business" : (row.businessName ?? "Removed business"));

const COLUMNS: DataTableColumn<AdminCostRow>[] = [
  {
    id: "business",
    header: "Business",
    sortValue: (row) => row.businessName,
    cell: (row) => (row.businessId === null ? <span className="text-ink-2">{rowName(row)}</span> : rowName(row)),
  },
  { id: "ai", header: "AI", align: "end", sortValue: (row) => row.aiMicros, cell: (row) => <span className="tabular-nums">{formatUsd(row.aiMicros)}</span> },
  {
    id: "twilio",
    header: "Twilio",
    align: "end",
    sortValue: (row) => row.twilioMicros,
    cell: (row) => <span className="tabular-nums">{row.twilioMicros === null ? "Not known" : formatUsd(row.twilioMicros)}</span>,
  },
  {
    id: "total",
    header: "Total",
    align: "end",
    sortValue: total,
    cell: (row) => {
      const micros = total(row);
      return <span className="font-semibold tabular-nums">{micros === null ? "Not known" : formatUsd(micros)}</span>;
    },
  },
  { id: "signIns", header: "Phone sign-ins", align: "end", hideOnPhone: true, sortValue: (row) => row.phoneSignIns, cell: (row) => <span className="tabular-nums">{row.phoneSignIns}</span> },
  { id: "aiCalls", header: "AI calls", align: "end", hideOnPhone: true, sortValue: (row) => row.aiCalls, cell: (row) => <span className="tabular-nums">{row.aiCalls}</span> },
];

export function CostsScreen() {
  const navigate = useNavigate();
  const months = useMemo(() => recentMonths(), []);
  const [month, setMonth] = useState(months[0]!);
  const costs = useAdminCosts(month);
  const data = costs.data;

  const balance = data?.twilioBalance;
  const low = balance && "usd" in balance && balance.usd < data.lowBalanceUsd;

  return (
    <>
      <PageHeader
        title="Costs"
        description="What Twilio and the AI cost for each business. The Twilio cost of a business is its share of the month's Twilio bill, by the phone sign-ins of its people. An SMS code counts more than a WhatsApp code."
      />
      <Toolbar>
        <Select label="Month" value={month} onChange={(event) => setMonth(event.target.value)} className="sm:max-w-[220px]">
          {months.map((value) => (
            <option key={value} value={value}>
              {monthName(value)}
            </option>
          ))}
        </Select>
      </Toolbar>

      {costs.error && !data ? (
        <ErrorState error={costs.error} onRetry={() => void costs.refetch()} />
      ) : (
        <>
          {costs.error && data ? (
            <div className="mb-4">
              <Notice tone="warning" title="These numbers may be old">
                The page could not read the costs again. The balance and the totals are from the last read.
              </Notice>
            </div>
          ) : null}
          {balance && !("usd" in balance) ? (
            <div className="mb-4">
              <Notice tone="warning" title="The Twilio balance cannot be read">
                {balance.unavailable}. Control Center reads it with a Main API key in Doppler (prd_cc): TWILIO_BALANCE_API_KEY_SID and TWILIO_BALANCE_API_KEY_SECRET.
              </Notice>
            </div>
          ) : null}
          {data && !("usd" in data.twilioTotal) ? (
            <div className="mb-4">
              <Notice tone="warning" title="The Twilio bill cannot be read">
                {data.twilioTotal.unavailable}. The Twilio cost of each business is not known.
              </Notice>
            </div>
          ) : null}

          <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3">
            <StatTile
              label="Twilio balance now"
              value={balance && "usd" in balance ? usd(balance.usd) : "Not known"}
              {...(balance && "usd" in balance && data
                ? { delta: `${aed(balance.usd)}. ${low ? "Below" : "Limit"} $${data.lowBalanceUsd.toFixed(2)}`, tone: low ? ("bad" as const) : ("neutral" as const) }
                : {})}
            />
            <StatTile
              label={`Twilio, ${monthName(month)}`}
              value={data && "usd" in data.twilioTotal ? usd(data.twilioTotal.usd) : "Not known"}
              {...(data && "usd" in data.twilioTotal ? { delta: aed(data.twilioTotal.usd) } : {})}
            />
            <StatTile
              label={`AI, ${monthName(month)}`}
              value={data ? formatUsd(data.aiTotalMicros) : "…"}
              {...(data ? { delta: aed(data.aiTotalMicros / 1_000_000) } : {})}
            />
          </div>

          <DataTable
            columns={COLUMNS}
            rows={data?.rows ?? []}
            rowKey={(row) => row.businessId ?? "none"}
            rowLabel={rowName}
            onRowClick={(row) => {
              if (row.businessId) void navigate({ to: "/businesses/$id", params: { id: row.businessId } });
            }}
            canOpenRow={(row) => row.businessId !== null}
            isLoading={costs.isLoading}
            caption={`Costs by business, ${monthName(month)}`}
            density="compact"
            countLabel={(count) => `${count} rows`}
            empty={{ title: "No costs", message: "No business used the AI or a phone sign-in in this month." }}
          />
        </>
      )}
    </>
  );
}
